import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { t } from '@lingui/core/macro';
import { Block } from '../classes/Block';

const SBS_PAIR_COLORS: readonly string[] = [
  '#2f7ebd',
  '#cc7a00',
  '#2f9d74',
  '#8e63ce',
  '#c05621',
  '#007f94',
] as const;

export interface SbsPairMeta {
  pairKey: string;
  pairNumber: number;
  startIndex: number;
  endIndex: number;
  leftIndex: number;
  rightIndex: number;
  leftBlockId: string;
  rightBlockId: string;
  color: string;
}

export interface SbsBlockMeta {
  pair: SbsPairMeta;
  side: 'left' | 'right';
  partnerIndex: number;
}

export interface SbsPairLookup {
  pairs: SbsPairMeta[];
  byIndex: Map<number, SbsBlockMeta>;
}

export interface BlockRangeMoveResult {
  blocks: Block[];
  movedRangeStart: number;
  movedRangeEnd: number;
  insertionIndex: number;
}

export interface CloneBlocksResult {
  blocks: Block[];
  insertedStartIndex: number;
  insertedEndIndex: number;
  selectedIndex: number;
}

export interface BlockMoveRange {
  startIndex: number;
  endIndex: number;
}

export interface SbsDropValidationResult {
  isBlocked: boolean;
  insertionIndex: number | null;
}

interface SbsDeleteDialogProps {
  open: boolean;
  blockName: string;
  partnerName: string;
  onDeleteBoth: () => void;
  onDeleteOnly: () => void;
  onCancel: () => void;
}

export function hasAnySbsBlocks(blocks: Block[]): boolean {
  return blocks.some((block: Block) => block.sbsBlocks !== 'normal');
}

export function buildSbsPairLookup(blocks: Block[]): SbsPairLookup {
  const pairs: SbsPairMeta[] = [];
  const byIndex: Map<number, SbsBlockMeta> = new Map<number, SbsBlockMeta>();

  for (let index = 0; index < blocks.length - 1; index += 1) {
    const currentBlock: Block = blocks[index];
    const nextBlock: Block = blocks[index + 1];
    const isCurrentSbsSide: boolean = currentBlock.sbsBlocks === 'left' || currentBlock.sbsBlocks === 'right';
    const isNextSbsSide: boolean = nextBlock.sbsBlocks === 'left' || nextBlock.sbsBlocks === 'right';
    const isAdjacentComplementaryPair: boolean =
      isCurrentSbsSide &&
      isNextSbsSide &&
      currentBlock.sbsBlocks !== nextBlock.sbsBlocks;

    if (!isAdjacentComplementaryPair) {
      continue;
    }

    const currentSide: 'left' | 'right' = currentBlock.sbsBlocks === 'left' ? 'left' : 'right';
    const nextSide: 'left' | 'right' = nextBlock.sbsBlocks === 'left' ? 'left' : 'right';
    const leftIndex: number = currentBlock.sbsBlocks === 'left' ? index : index + 1;
    const rightIndex: number = currentBlock.sbsBlocks === 'right' ? index : index + 1;
    const pairNumber: number = pairs.length + 1;
    const pair: SbsPairMeta = {
      pairKey: `${currentBlock.id}:${nextBlock.id}`,
      pairNumber,
      startIndex: index,
      endIndex: index + 1,
      leftIndex,
      rightIndex,
      leftBlockId: blocks[leftIndex].id,
      rightBlockId: blocks[rightIndex].id,
      color: SBS_PAIR_COLORS[(pairNumber - 1) % SBS_PAIR_COLORS.length],
    };

    pairs.push(pair);
    byIndex.set(index, {
      pair,
      side: currentSide,
      partnerIndex: index + 1,
    });
    byIndex.set(index + 1, {
      pair,
      side: nextSide,
      partnerIndex: index,
    });

    index += 1;
  }

  return { pairs, byIndex };
}

export function getMoveRangeForBlockIndex(blocks: Block[], blockIndex: number): BlockMoveRange {
  const sbsMeta: SbsBlockMeta | undefined = buildSbsPairLookup(blocks).byIndex.get(blockIndex);
  if (!sbsMeta) {
    return { startIndex: blockIndex, endIndex: blockIndex };
  }

  return {
    startIndex: sbsMeta.pair.startIndex,
    endIndex: sbsMeta.pair.endIndex,
  };
}

export function moveBlockRange(
  blocks: Block[],
  startIndex: number,
  endIndex: number,
  targetIndex: number
): BlockRangeMoveResult | null {
  if (
    startIndex < 0 ||
    endIndex < startIndex ||
    endIndex >= blocks.length ||
    targetIndex < 0 ||
    targetIndex >= blocks.length
  ) {
    return null;
  }

  if (targetIndex >= startIndex && targetIndex <= endIndex) {
    return null;
  }

  const movedBlocks: Block[] = blocks.slice(startIndex, endIndex + 1);
  const remainingBlocks: Block[] = [
    ...blocks.slice(0, startIndex),
    ...blocks.slice(endIndex + 1),
  ];
  const movedCount: number = movedBlocks.length;
  const insertionIndex: number =
    targetIndex < startIndex
      ? targetIndex
      : targetIndex - movedCount + 1;

  const reorderedBlocks: Block[] = [
    ...remainingBlocks.slice(0, insertionIndex),
    ...movedBlocks,
    ...remainingBlocks.slice(insertionIndex),
  ];

  return {
    blocks: reorderedBlocks,
    movedRangeStart: insertionIndex,
    movedRangeEnd: insertionIndex + movedCount - 1,
    insertionIndex,
  };
}

export function getSbsDropValidation(
  blocks: Block[],
  startIndex: number,
  endIndex: number,
  targetIndex: number
): SbsDropValidationResult {
  if (
    startIndex < 0 ||
    endIndex < startIndex ||
    endIndex >= blocks.length ||
    targetIndex < 0 ||
    targetIndex >= blocks.length
  ) {
    return {
      isBlocked: false,
      insertionIndex: null,
    };
  }

  if (targetIndex >= startIndex && targetIndex <= endIndex) {
    return {
      isBlocked: false,
      insertionIndex: null,
    };
  }

  const movedCount: number = endIndex - startIndex + 1;
  const remainingBlocks: Block[] = [
    ...blocks.slice(0, startIndex),
    ...blocks.slice(endIndex + 1),
  ];
  const insertionIndex: number =
    targetIndex < startIndex
      ? targetIndex
      : targetIndex - movedCount + 1;
  const remainingLookup: SbsPairLookup = buildSbsPairLookup(remainingBlocks);
  const isBlocked: boolean = remainingLookup.pairs.some((pair: SbsPairMeta) => pair.endIndex === insertionIndex);

  return {
    isBlocked,
    insertionIndex,
  };
}

export function remapIndexAfterRangeMove(
  index: number,
  startIndex: number,
  endIndex: number,
  targetIndex: number
): number {
  const movedCount: number = endIndex - startIndex + 1;
  const insertionIndex: number =
    targetIndex < startIndex
      ? targetIndex
      : targetIndex - movedCount + 1;

  if (index >= startIndex && index <= endIndex) {
    return insertionIndex + (index - startIndex);
  }

  if (targetIndex < startIndex && index >= targetIndex && index < startIndex) {
    return index + movedCount;
  }

  if (targetIndex > endIndex && index > endIndex && index <= targetIndex) {
    return index - movedCount;
  }

  return index;
}

export function getUniqueBlockName(baseName: string, existingNames: Iterable<string>): string {
  const normalizedBaseName: string = baseName.trim();
  const usedNames: Set<string> = new Set<string>(
    Array.from(existingNames, (name: string) => name.trim())
  );
  if (!usedNames.has(normalizedBaseName)) {
    return normalizedBaseName;
  }

  let suffix: number = 2;
  while (usedNames.has(`${normalizedBaseName} (${suffix})`)) {
    suffix += 1;
  }
  return `${normalizedBaseName} (${suffix})`;
}

function cloneBlock(block: Block, name: string): Block {
  return Block.create({
    ...block,
    id: undefined,
    name,
    dataRows: block.dataRows.map((row) => ({ ...row })),
  });
}

export function cloneBlocksForSource(
  blocks: Block[],
  sourceIndex: number,
  copyLabel: string,
  includeSbsPair: boolean = true
): CloneBlocksResult | null {
  if (sourceIndex < 0 || sourceIndex >= blocks.length) {
    return null;
  }

  const sbsMeta: SbsBlockMeta | undefined = buildSbsPairLookup(blocks).byIndex.get(sourceIndex);
  const sourceBlocks: Block[] = sbsMeta && includeSbsPair
    ? blocks.slice(sbsMeta.pair.startIndex, sbsMeta.pair.endIndex + 1)
    : [blocks[sourceIndex]];
  const existingNames: Set<string> = new Set<string>(blocks.map((block: Block) => block.name));
  const clones: Block[] = sourceBlocks.map((block: Block) => {
    const name: string = getUniqueBlockName(`${block.name} ${copyLabel}`, existingNames);
    existingNames.add(name);
    return cloneBlock(block, name);
  });
  const insertedStartIndex: number = blocks.length;
  const insertedEndIndex: number = insertedStartIndex + clones.length - 1;
  const selectedIndex: number = sbsMeta && includeSbsPair
    ? insertedStartIndex + (sourceIndex - sbsMeta.pair.startIndex)
    : insertedStartIndex;

  return {
    blocks: [...blocks, ...clones],
    insertedStartIndex,
    insertedEndIndex,
    selectedIndex,
  };
}

export const SbsDeleteDialog: React.FC<SbsDeleteDialogProps> = ({
  open,
  blockName,
  partnerName,
  onDeleteBoth,
  onDeleteOnly,
  onCancel,
}) => {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t`Delete side-by-side pair?`}</DialogTitle>
      <DialogContent>
        {t`"${blockName}" is paired with "${partnerName}".`}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t`Cancel`}</Button>
        <Button onClick={onDeleteOnly}>{t`Delete only this block`}</Button>
        <Button onClick={onDeleteBoth} color="error" variant="contained">
          {t`Delete both`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
